/**
 * The `/welcome` section (RO-3-12 / #44305 + #44576).
 *
 * ── Why `/welcome` must be a REAL page, not a redirect ──
 *
 * Shell gate 1 (`shell/redirects.ts`) bounces EVERY path to `/welcome` while
 * `onboardingActive`. Until this route is mounted, a fresh install 404s on any
 * URL the user opens — the SPA serves `index.html` at 200 for the not-found
 * boundary, so the break is a blank shell, not a status code.
 *
 * The full onboarding-v2 arc (`app/welcome/_v2/arc.tsx`, ~3k LOC over the
 * detect/enrich/advisor engines) is still the long-term body. Porting that
 * closure is a separate item; this hub is the minimal real destination that
 * keeps first-run reachable: it reads live onboarding state, shows where the
 * arc is, and offers "Enter Excellent" via the already-mounted
 * `POST /api/welcome/finish` (RO-2-07).
 *
 * ── `/welcome/$step` ──
 *
 * Port of `app/welcome/[step]/page.tsx` — pure redirect to the hub. The 15-step
 * wizard is gone; legacy `current_step` ids (incl. the frozen DDL default
 * `'info'`) still exist in every install, so the segment can never 404.
 */

import * as React from "react";
import { createRoute, redirect, useNavigate } from "@tanstack/react-router";
import { mutate } from "swr";
import { Button, Card, CardContent, PageHeader } from "@excellent/ui";
import { useApi, apiCall, apiKey } from "@excellent/ui/api";
import { onboarding } from "@excellent/ui/api/contracts/onboarding";
import { welcome } from "@excellent/ui/api/contracts/welcome";
import { RouterLink as Link } from "@excellent/ui/router";
import { rootRoute } from "./__root";

/**
 * Minimal real hub — not an empty stub. Live state from GET /api/onboarding,
 * finish via POST /api/welcome/finish (PRD §9 lands on HOME).
 */
function WelcomeHub() {
  const navigate = useNavigate();
  welcomeIndexRoute.useSearch();
  const scopedArgs = {};
  const { data, error, isLoading } = useApi(onboarding.get, scopedArgs);
  const state = data?.state;
  const [finishing, setFinishing] = React.useState(false);
  const [finishError, setFinishError] = React.useState<string | null>(null);

  const completed = Boolean(state?.completed_at);
  const step = state?.current_step ?? "info";

  async function enterExcellent() {
    setFinishing(true);
    setFinishError(null);
    try {
      const res = await apiCall(welcome.finishOnboarding, scopedArgs);
      await mutate(apiKey(onboarding.get, scopedArgs));
      const dest = res?.redirectTo ?? "/";
      void navigate({ to: dest, replace: true });
    } catch (e) {
      setFinishError(e instanceof Error ? e.message : String(e));
      setFinishing(false);
    }
  }

  return (
    <main
      className="mx-auto flex max-w-xl flex-col gap-6 px-6 py-12"
      data-testid="welcome-hub"
    >
      <PageHeader
        title="Welcome to Excellent"
        description="First-run setup. Confirm your workspace, then enter the app."
      />

      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading onboarding state…</p>
      ) : null}
      {error ? (
        <p className="type-body text-destructive" role="alert">
          {error.message ?? String(error)}
        </p>
      ) : null}
      {state ? (
        <Card data-testid="welcome-state" className="gap-3 py-4">
          <CardContent className="flex flex-col gap-3 px-4">
            <p className="type-body text-foreground">
              <span className="text-muted-foreground">Station · </span>
              <span className="font-mono" data-testid="welcome-step">
                {step}
              </span>
            </p>
            {state.display_name ? (
              <p className="type-body text-foreground">
                <span className="text-muted-foreground">You · </span>
                {state.display_name}
              </p>
            ) : null}
            {state.company_domain ? (
              <p className="type-body text-foreground">
                <span className="text-muted-foreground">Domain · </span>
                <span className="font-mono">{state.company_domain}</span>
              </p>
            ) : null}
            {completed ? (
              <p className="type-body text-muted-foreground">
                Setup is complete. You can enter the app or revisit any time.
              </p>
            ) : (
              <p className="type-body text-muted-foreground">
                The full five-station arc (scan → plan) still lives on the Next
                host until its client port lands. This hub keeps the onboarding
                bounce on real content instead of a blank shell.
              </p>
            )}
          </CardContent>
        </Card>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={() => void enterExcellent()}
          disabled={finishing}
          data-testid="welcome-enter"
        >
          {finishing ? "Entering…" : "Enter Excellent"}
        </Button>
        <Button variant="outline" asChild>
          <Link href="/transparency">What we collect</Link>
        </Button>
      </div>

      {finishError ? (
        <p className="type-body text-destructive" role="alert">
          {finishError}
        </p>
      ) : null}
    </main>
  );
}

export const welcomeIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/welcome",
  component: WelcomeHub,
});

/**
 * Port of `app/welcome/[step]/page.tsx` — pure redirect to the hub.
 * Destination is the real `/welcome` page above (not a future hole).
 */
export const welcomeStepRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/welcome/$step",
  beforeLoad: () => {
    throw redirect({ to: "/welcome", replace: true });
  },
});
